import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormGroup, FormControl, ControlLabel, Button } from 'react-bootstrap';
import format from 'date-fns/format';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import RoleSkillSelect from 'components/Shared/RoleSkillSelect';
import { load as loadSuggestedRoles } from 'redux/modules/suggestedRoles2';
import styles from './EditJobModal.module.scss';

const EditJobForm = ({ brief, saving, onCancel, onSubmit, type }) => {
  const [formData, setFormData] = useState({
    jobTitle: '',
    description: '',
    role: '',
    location: '',
    locationText: '',
    remote: 0,
    budget: '',
    closingDate: '',
  });

  const [errors, setErrors] = useState({});
  const [specQuestionsSuffix, setSpecQuestionsSuffix] = useState('');

  const dispatch = useDispatch();

  // Suggested roles (IDs) from redux
  const {
    loaded: loadedSuggestedRoles,
    loading: loadingSuggestedRoles,
    suggestedRoles = [],
  } = useSelector((state) => state.suggestedRoles2 || {});

  // Role entities
  const roleEntities = useSelector(
    (state) => (state.entities && state.entities.roles) || {}
  );

  // Map IDs to full role/skill objects expected by RoleSkillSelect
  const roleOptions = Array.isArray(suggestedRoles)
    ? suggestedRoles
        .map((id) => roleEntities[id])
        .filter((role) => role && typeof role.name === 'string')
    : [];

  useEffect(() => {
    if (brief) {
      let closingDateFormatted = '';
      if (brief.expires_at) {
        const date = new Date(brief.expires_at);
        closingDateFormatted = date.toISOString().split('T')[0];
      }

      // Hides the questions section from the description as we do not want this to be editable
      // This is a temporary solution until we have a better way to handle questions
      const fullSpec = brief.spec || '';
      const marker = '**In what capacity are you hiring?**';
      const markerIndex =
        typeof fullSpec === 'string' ? fullSpec.indexOf(marker) : -1;

      const descriptionWithoutQuestions =
        markerIndex !== -1
          ? fullSpec.slice(0, markerIndex).trimEnd()
          : fullSpec;
      const questionsSuffix =
        markerIndex !== -1 ? fullSpec.slice(markerIndex).trimStart() : '';

      setFormData({
        jobTitle: brief.text || '',
        description: descriptionWithoutQuestions,
        role: brief.role || '',
        location: brief.location || '',
        locationText: brief.location?.text || brief.location || '',
        remote: brief.remote || 0,
        budget: brief.budget && brief.budget > 0 ? brief.budget / 100 : '',
        closingDate: closingDateFormatted,
      });
      setSpecQuestionsSuffix(questionsSuffix);
      setErrors({});
    }
  }, [brief]);

  // Ensure suggested roles are loaded
  useEffect(() => {
    if (!loadedSuggestedRoles && !loadingSuggestedRoles) {
      dispatch(loadSuggestedRoles());
    }
  }, [loadedSuggestedRoles, loadingSuggestedRoles, dispatch]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.description.trim())
      newErrors.description = 'Description is required';
    if (!formData.locationText.trim() && !formData.remote)
      newErrors.location = 'Location is required';
    if (!formData.closingDate)
      newErrors.closingDate = 'Job closing date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const closingDateTime = new Date(formData.closingDate);
    closingDateTime.setHours(23, 59, 59, 999);

    // Re-attach the original questions suffix (if any) after the editable description
    let spec = formData.description || '';
    if (specQuestionsSuffix) {
      const trimmedDesc = spec.trimEnd();
      spec = trimmedDesc
        ? `${trimmedDesc}\n\n${specQuestionsSuffix}`
        : specQuestionsSuffix;
    }

    const payload = {
      text: formData.jobTitle,
      spec,
      location_text: formData.locationText,
      remote: formData.remote,
      amount: formData.budget ? parseFloat(formData.budget) * 100 : -1,
      expires_at: format(closingDateTime, 'yyyy-MM-dd HH:mm:ss'),
    };

    // Only include role, currency, and questions when duplicating a job
    if (type === 'duplicate') {
      payload.role = formData.role;
      payload.currency = brief.currency;
      if (Array.isArray(brief.questions) && brief.questions.length > 0) {
        // Manual mapping to questions in suggested_roles_questions table
        payload.questions = {
          1: brief.questions[0].answer,
          2: brief.questions[1].answer,
          6: brief.questions[2].answer,
          7: brief.questions[3].answer,
          126: brief.questions[4].answer,
        };
      }
    }

    onSubmit(payload);
  };

  const handleCancelClick = () => {
    setFormData({
      jobTitle: '',
      description: '',
      location: '',
      locationText: '',
      remote: 0,
      budget: '',
      closingDate: '',
    });
    setErrors({});
    onCancel();
  };

  const handleLocationChange = (location) => {
    if (!location.country) {
      // No country provided at all — treat as remote/unspecified
      setFormData((prev) => ({ ...prev, remote: 1, locationText: '' }));
    } else {
      // Country-only or city+country selections both set a location text
      const locationText = location.city
        ? location.city + ', ' + location.country
        : location.country;
      setFormData((prev) => ({
        ...prev,
        remote: 0,
        locationText,
      }));
    }
    if (errors.location) setErrors((prev) => ({ ...prev, location: null }));
  };

  const handleLocationRemote = () => {
    setFormData((prev) => ({ ...prev, remote: 1, locationText: '' }));
    if (errors.location) setErrors((prev) => ({ ...prev, location: null }));
  };

  if (!brief) return null;

  const submitButtonText =
    type === 'duplicate'
      ? saving
        ? 'Creating New Job...'
        : 'Duplicate Job'
      : saving
      ? 'Saving...'
      : 'Save Changes';

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <FormGroup
        controlId="jobTitle"
        className={errors.jobTitle ? styles.hasError : ''}
      >
        <ControlLabel className={styles.label}>Job Title</ControlLabel>
        <FormControl
          type="text"
          value={formData.jobTitle}
          onChange={(e) => handleChange('jobTitle', e.target.value)}
          placeholder="Senior Frontend Developer"
          className={styles.input}
          disabled={saving}
        />
        {errors.jobTitle && (
          <div className={styles.errorText}>{errors.jobTitle}</div>
        )}
      </FormGroup>

      {type === 'duplicate' && (
        <FormGroup controlId="role">
          <ControlLabel className={styles.label}>Role</ControlLabel>
          <RoleSkillSelect
            roles={roleOptions}
            skills={[]} // Skills are no longer supported in the job posting form
            loadingRoles={loadingSuggestedRoles}
            loadingSkills={false}
            value={
              formData.role
                ? { selectedRole: { name: formData.role } }
                : undefined
            }
            onValueChange={(selected) =>
              handleChange(
                'role',
                selected && selected.name ? selected.name : ''
              )
            }
            placeholder="Type a freelance role or skill"
          />
        </FormGroup>
      )}

      <FormGroup
        controlId="description"
        className={errors.description ? styles.hasError : ''}
      >
        <ControlLabel className={styles.label}>Description</ControlLabel>
        <FormControl
          componentClass="textarea"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="We are looking for an experienced Frontend Developer to join our team and help build the next generation of our product."
          className={styles.textarea}
          disabled={saving}
          rows={8}
        />
        {errors.description && (
          <div className={styles.errorText}>{errors.description}</div>
        )}
      </FormGroup>

      <FormGroup
        controlId="location"
        className={errors.location ? styles.hasError : ''}
      >
        <ControlLabel className={styles.label}>
          Will your freelancer be required onsite?
        </ControlLabel>
        <SingleLocationDropdown
          defaultLocation={formData.location}
          onLocationChange={handleLocationChange}
          onLocationReset={handleLocationRemote}
          placeholder="Enter location or leave empty for remote"
          showResetIcon={true}
          shouldClearOnBlur={true}
          shouldResetOnBlur={true}
          disabled={saving}
        />
        {errors.location && (
          <div className={styles.errorText}>{errors.location}</div>
        )}
      </FormGroup>

      <FormGroup
        controlId="budget"
        className={errors.budget ? styles.hasError : ''}
      >
        <ControlLabel className={styles.label}>Budget</ControlLabel>
        <div className={styles.budgetInputWrapper}>
          <FormControl
            type="number"
            value={formData.budget}
            onChange={(e) => handleChange('budget', e.target.value)}
            placeholder="120000"
            className={`${styles.input} ${styles.budgetInput}`}
            disabled={saving}
            min="0"
            step="0.01"
          />
          <span className={styles.currencySymbol}>
            {brief.currency === 'usd'
              ? '$'
              : brief.currency === 'gbp'
              ? '£'
              : '€'}
          </span>
        </div>
        {errors.budget && (
          <div className={styles.errorText}>{errors.budget}</div>
        )}
      </FormGroup>

      <FormGroup
        controlId="closingDate"
        className={errors.closingDate ? styles.hasError : ''}
      >
        <ControlLabel className={styles.label}>Job Closing Date</ControlLabel>
        <FormControl
          type="date"
          value={formData.closingDate}
          onChange={(e) => handleChange('closingDate', e.target.value)}
          className={styles.input}
          disabled={saving}
          min={new Date().toISOString().split('T')[0]}
        />
        {errors.closingDate && (
          <div className={styles.errorText}>{errors.closingDate}</div>
        )}
      </FormGroup>

      {type === 'duplicate' && Array.isArray(brief.questions) && (
        <div className={styles.questionsSection}>
          <h3 className={styles.questionsSectionTitle}>
            Questions on this job
          </h3>
          <p className={styles.questionsSectionHint}>
            To change your answers, please create a new job posting{' '}
            <a href="/post-a-job" className={styles.questionsSectionLink}>
              here
            </a>
            .
          </p>
          <div className={styles.questionsListDivider} />
          {brief.questions.map((q, index) => {
            let answerText = '';
            if (typeof q.answer === 'string') {
              answerText = q.answer.trim();
            } else if (Array.isArray(q.answer)) {
              answerText = q.answer
                .filter((line) => typeof line === 'string')
                .map((line) => line.trim())
                .filter((line) => line.length > 0)
                .join(', ');
            }

            return (
              <div key={q.id ?? index} className={styles.questionReadOnlyItem}>
                <div className={styles.questionReadOnlyLabel}>{q.question}</div>
                <div className={styles.questionReadOnlyAnswer}>
                  {answerText || 'No answer given'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.buttonContainer}>
        <Button
          type="button"
          onClick={handleCancelClick}
          disabled={saving}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          bsStyle="primary"
          disabled={saving}
          className={styles.saveButton}
        >
          {submitButtonText}
        </Button>
      </div>
    </form>
  );
};

EditJobForm.propTypes = {
  brief: PropTypes.object,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['edit', 'duplicate']),
  contentBeforeButtons: PropTypes.node,
};

EditJobForm.defaultProps = {
  brief: null,
  saving: false,
  type: 'edit',
  contentBeforeButtons: null,
};

export default EditJobForm;
